import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getInvoicingSummary,
  getDocumentTypes,
  getReceiptTypes,
  getPaymentTypes,
  getRivhitCustomers,
  listInvoices,
  listReceipts,
  createInvoice,
  createReceipt,
} from '../api/client';
import {
  clearRivhitDocumentsDirty,
  getRivhitDocumentsDirtyAt,
  subscribeToRivhitDocumentsDirty,
} from '../lib/rivhitDocumentsSync';

// ── Types ────────────────────────────────────────────────────────────

interface Summary {
  total_invoices: number;
  total_invoices_amount: number;
  total_receipts: number;
  total_receipts_amount: number;
  vat_rate: number;
}

interface DocumentListItem {
  document_type: number;
  document_number: number;
  document_date: string;
  document_time: string;
  amount: number;
  customer_id: number;
  agent_id?: number;
}

interface DocType {
  document_type: number;
  document_name: string;
  is_invoice_receipt: boolean;
  is_accounting: boolean;
  price_include_vat: boolean;
}

interface ReceiptType {
  receipt_type: number;
  receipt_name: string;
  is_invoice_receipt: boolean;
}

interface PaymentTypeItem {
  payment_type: number;
  payment_name: string;
  type_code: number;
}

interface Customer {
  customer_id: number;
  last_name: string;
  first_name: string;
  email?: string;
  phone?: string;
}

// ── Tabs ─────────────────────────────────────────────────────────────

type Tab = 'summary' | 'invoices' | 'receipts' | 'create_invoice' | 'create_receipt';

// ── Component ────────────────────────────────────────────────────────

export default function Invoicing() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('summary');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [summary, setSummary] = useState<Summary | null>(null);
  const [invoices, setInvoices] = useState<DocumentListItem[]>([]);
  const [receipts, setReceipts] = useState<DocumentListItem[]>([]);
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [receiptTypes, setReceiptTypes] = useState<ReceiptType[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeItem[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);

  // ── Loaders ──────────────────────────────────────────────────────

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getInvoicingSummary();
      setSummary(res.data);
    } catch (e: unknown) {
      setError(String((e as Error).message || 'Failed to load summary'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listInvoices();
      setInvoices(res.data ?? []);
    } catch (e: unknown) {
      setError(String((e as Error).message || 'Failed to load invoices'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReceipts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listReceipts();
      setReceipts(res.data ?? []);
    } catch (e: unknown) {
      setError(String((e as Error).message || 'Failed to load receipts'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRefData = useCallback(async () => {
    try {
      const [dtRes, rtRes, ptRes, custRes] = await Promise.all([
        getDocumentTypes(),
        getReceiptTypes(),
        getPaymentTypes(),
        getRivhitCustomers(),
      ]);
      setDocTypes(dtRes.data ?? []);
      setReceiptTypes((rtRes.data ?? []).filter((rt: ReceiptType) => !rt.is_invoice_receipt));
      setPaymentTypes(ptRes.data ?? []);
      setCustomers(custRes.data ?? []);
    } catch {
      // non-critical
    }
  }, []);

  const refreshCurrentTab = useCallback(async () => {
    await loadRefData();

    if (tab === 'summary') {
      await loadSummary();
      return;
    }

    if (tab === 'invoices') {
      await Promise.all([loadInvoices(), loadSummary()]);
      return;
    }

    if (tab === 'receipts') {
      await Promise.all([loadReceipts(), loadSummary()]);
      return;
    }

    await loadSummary();
  }, [loadInvoices, loadReceipts, loadRefData, loadSummary, tab]);

  useEffect(() => {
    loadSummary();
    loadRefData();
  }, [loadSummary, loadRefData]);

  useEffect(() => {
    if (tab === 'invoices') loadInvoices();
    if (tab === 'receipts') loadReceipts();
  }, [tab, loadInvoices, loadReceipts]);

  useEffect(() => {
    const maybeRefreshDirtyData = () => {
      const dirtyAt = getRivhitDocumentsDirtyAt();
      if (!dirtyAt) return;
      setRefreshTick((prev) => prev + 1);
    };

    maybeRefreshDirtyData();
    return subscribeToRivhitDocumentsDirty(maybeRefreshDirtyData);
  }, []);

  useEffect(() => {
    if (!refreshTick) return;

    refreshCurrentTab().finally(() => {
      clearRivhitDocumentsDirty();
    });
  }, [refreshCurrentTab, refreshTick]);

  // ── Tab bar ──────────────────────────────────────────────────────

  const tabs: { key: Tab; label: string }[] = [
    { key: 'summary', label: t('invoicing.tabs.summary', 'Summary') },
    { key: 'invoices', label: t('invoicing.tabs.invoices', 'Invoices') },
    { key: 'receipts', label: t('invoicing.tabs.receipts', 'Receipts') },
    { key: 'create_invoice', label: t('invoicing.tabs.createInvoice', '+ Invoice') },
    { key: 'create_receipt', label: t('invoicing.tabs.createReceipt', '+ Receipt') },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold mb-1">{t('invoicing.title', 'Invoicing')}</h1>
          <p className="text-gray-500 text-sm">{t('invoicing.subtitle', 'Manage invoices and receipts via Rivhit')}</p>
        </div>
        <button
          type="button"
          onClick={refreshCurrentTab}
          disabled={loading}
          className="inline-flex items-center gap-1.5 self-start rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className={loading ? 'animate-spin' : ''}>🔄</span>
          {t('common.refresh', 'Refresh')}
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b mb-6 overflow-x-auto">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              tab === key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      {loading && tab !== 'create_invoice' && tab !== 'create_receipt' && (
        <div className="text-center py-12 text-gray-400">{t('common.loading', 'Loading...')}</div>
      )}

      {!loading && tab === 'summary' && summary && <SummaryPanel summary={summary} />}
      {!loading && tab === 'invoices' && <InvoiceList invoices={invoices} docTypes={docTypes} />}
      {!loading && tab === 'receipts' && <ReceiptList receipts={receipts} />}
      {tab === 'create_invoice' && (
        <CreateInvoiceForm
          docTypes={docTypes}
          customers={customers}
          onCreated={() => { setTab('invoices'); loadSummary(); }}
        />
      )}
      {tab === 'create_receipt' && (
        <CreateReceiptForm
          receiptTypes={receiptTypes}
          paymentTypes={paymentTypes}
          customers={customers}
          onCreated={() => { setTab('receipts'); loadSummary(); }}
        />
      )}
    </div>
  );
}

// ── Summary Panel ────────────────────────────────────────────────────

function SummaryPanel({ summary }: { summary: Summary }) {
  const { t } = useTranslation();
  const cards = [
    { label: t('invoicing.totalInvoices', 'Total Invoices'), value: summary.total_invoices, color: 'bg-blue-50 text-blue-700' },
    { label: t('invoicing.invoicesAmount', 'Invoices Amount'), value: `₪${summary.total_invoices_amount.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`, color: 'bg-green-50 text-green-700' },
    { label: t('invoicing.totalReceipts', 'Total Receipts'), value: summary.total_receipts, color: 'bg-purple-50 text-purple-700' },
    { label: t('invoicing.receiptsAmount', 'Receipts Amount'), value: `₪${summary.total_receipts_amount.toLocaleString('he-IL', { minimumFractionDigits: 2 })}`, color: 'bg-orange-50 text-orange-700' },
    { label: t('invoicing.vatRate', 'VAT Rate'), value: `${summary.vat_rate}%`, color: 'bg-gray-50 text-gray-700' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {cards.map((c) => (
        <div key={c.label} className={`rounded-lg p-4 ${c.color}`}>
          <div className="text-xs font-medium opacity-75 mb-1">{c.label}</div>
          <div className="text-2xl font-bold">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ── Invoice List ─────────────────────────────────────────────────────

function InvoiceList({ invoices, docTypes }: { invoices: DocumentListItem[]; docTypes: DocType[] }) {
  const { t } = useTranslation();
  const docTypeName = (type: number) => docTypes.find((d) => d.document_type === type)?.document_name ?? `Type ${type}`;

  if (!invoices.length) {
    return <div className="text-center py-12 text-gray-400">{t('invoicing.noInvoices', 'No invoices found')}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-gray-500">
            <th className="py-2 px-3">#</th>
            <th className="py-2 px-3">{t('invoicing.type', 'Type')}</th>
            <th className="py-2 px-3">{t('invoicing.date', 'Date')}</th>
            <th className="py-2 px-3">{t('invoicing.time', 'Time')}</th>
            <th className="py-2 px-3">{t('invoicing.customer', 'Customer')}</th>
            <th className="py-2 px-3 text-right">{t('invoicing.amount', 'Amount')}</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv, i) => (
            <tr key={`${inv.document_type}-${inv.document_number}-${i}`} className="border-b hover:bg-gray-50">
              <td className="py-2 px-3 font-mono">{inv.document_number}</td>
              <td className="py-2 px-3">{docTypeName(inv.document_type)}</td>
              <td className="py-2 px-3">{inv.document_date}</td>
              <td className="py-2 px-3">{inv.document_time}</td>
              <td className="py-2 px-3">{inv.customer_id}</td>
              <td className="py-2 px-3 text-right font-mono">₪{inv.amount?.toLocaleString('he-IL', { minimumFractionDigits: 2 })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Receipt List ─────────────────────────────────────────────────────

function ReceiptList({ receipts }: { receipts: DocumentListItem[] }) {
  const { t } = useTranslation();

  if (!receipts.length) {
    return <div className="text-center py-12 text-gray-400">{t('invoicing.noReceipts', 'No receipts found')}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-gray-500">
            <th className="py-2 px-3">#</th>
            <th className="py-2 px-3">{t('invoicing.type', 'Type')}</th>
            <th className="py-2 px-3">{t('invoicing.date', 'Date')}</th>
            <th className="py-2 px-3">{t('invoicing.time', 'Time')}</th>
            <th className="py-2 px-3">{t('invoicing.customer', 'Customer')}</th>
            <th className="py-2 px-3 text-right">{t('invoicing.amount', 'Amount')}</th>
          </tr>
        </thead>
        <tbody>
          {receipts.map((r, i) => (
            <tr key={`${r.document_type}-${r.document_number}-${i}`} className="border-b hover:bg-gray-50">
              <td className="py-2 px-3 font-mono">{r.document_number}</td>
              <td className="py-2 px-3">{r.document_type}</td>
              <td className="py-2 px-3">{r.document_date}</td>
              <td className="py-2 px-3">{r.document_time}</td>
              <td className="py-2 px-3">{r.customer_id}</td>
              <td className="py-2 px-3 text-right font-mono">₪{r.amount?.toLocaleString('he-IL', { minimumFractionDigits: 2 })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Create Invoice Form ──────────────────────────────────────────────

function CreateInvoiceForm({
  docTypes,
  customers,
  onCreated,
}: {
  docTypes: DocType[];
  customers: Customer[];
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [documentType, setDocumentType] = useState(1);
  const [customerId, setCustomerId] = useState(0);
  const [priceIncludeVat, setPriceIncludeVat] = useState(true);
  const [comments, setComments] = useState('');
  const [items, setItems] = useState([{ description: '', quantity: 1, price_nis: 0 }]);

  // Payment fields for invoice-receipt (type 2)
  const isInvoiceReceipt = docTypes.find((d) => d.document_type === documentType)?.is_invoice_receipt ?? false;
  const [paymentType, setPaymentType] = useState(2); // cash default
  const [paymentAmount, setPaymentAmount] = useState(0);

  const addItem = () => setItems([...items, { description: '', quantity: 1, price_nis: 0 }]);
  const removeItem = (i: number) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i: number, field: string, value: string | number) =>
    setItems(items.map((item, idx) => (idx === i ? { ...item, [field]: value } : item)));

  const totalAmount = items.reduce((sum, it) => sum + it.quantity * it.price_nis, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) {
      setError(t('invoicing.selectCustomer', 'Please select a customer'));
      return;
    }
    if (!items.length || items.every((it) => !it.description && !it.price_nis)) {
      setError(t('invoicing.addItems', 'Please add at least one item'));
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    const payload: Record<string, unknown> = {
      document_type: documentType,
      customer_id: customerId,
      price_include_vat: priceIncludeVat,
      comments: comments || undefined,
      items: items.map((it) => ({
        description: it.description,
        quantity: it.quantity,
        price_nis: it.price_nis,
      })),
      send_mail: false,
    };

    if (isInvoiceReceipt) {
      payload.payments = [{ payment_type: paymentType, amount_nis: paymentAmount || totalAmount }];
    }

    try {
      const res = await createInvoice(payload);
      const data = res.data;
      setSuccess(
        `${t('invoicing.created', 'Created')} #${data.document_number}` +
          (data.document_link ? ` — PDF: ${data.document_link}` : '')
      );
      setTimeout(onCreated, 1500);
    } catch (e: unknown) {
      setError(String((e as Error).message || 'Failed to create invoice'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-5">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded text-sm">{success}</div>}

      {/* Doc type & customer */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoicing.docType', 'Document Type')}</label>
          <select
            value={documentType}
            onChange={(e) => setDocumentType(Number(e.target.value))}
            className="w-full border rounded-md px-3 py-2 text-sm"
          >
            {docTypes.map((dt) => (
              <option key={dt.document_type} value={dt.document_type}>
                {dt.document_name} ({dt.document_type})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoicing.customer', 'Customer')}</label>
          <select
            value={customerId}
            onChange={(e) => setCustomerId(Number(e.target.value))}
            className="w-full border rounded-md px-3 py-2 text-sm"
          >
            <option value={0}>-- {t('invoicing.selectCustomer', 'Select customer')} --</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.last_name} {c.first_name} ({c.customer_id})
              </option>
            ))}
          </select>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={priceIncludeVat} onChange={(e) => setPriceIncludeVat(e.target.checked)} />
        {t('invoicing.priceIncludeVat', 'Price includes VAT')}
      </label>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoicing.comments', 'Comments')}</label>
        <input
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          className="w-full border rounded-md px-3 py-2 text-sm"
          maxLength={400}
        />
      </div>

      {/* Items */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700">{t('invoicing.items', 'Items')}</label>
          <button type="button" onClick={addItem} className="text-blue-600 text-sm hover:underline">
            + {t('invoicing.addItem', 'Add item')}
          </button>
        </div>
        {items.map((item, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input
              placeholder={t('invoicing.description', 'Description')}
              value={item.description}
              onChange={(e) => updateItem(i, 'description', e.target.value)}
              className="flex-1 border rounded-md px-3 py-2 text-sm"
            />
            <input
              type="number"
              placeholder={t('invoicing.qty', 'Qty')}
              value={item.quantity}
              onChange={(e) => updateItem(i, 'quantity', Number(e.target.value))}
              className="w-20 border rounded-md px-3 py-2 text-sm"
              min={1}
            />
            <input
              type="number"
              placeholder={t('invoicing.price', 'Price')}
              value={item.price_nis || ''}
              onChange={(e) => updateItem(i, 'price_nis', Number(e.target.value))}
              className="w-28 border rounded-md px-3 py-2 text-sm"
              step="0.01"
              min={0}
            />
            {items.length > 1 && (
              <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 px-1">
                ✕
              </button>
            )}
          </div>
        ))}
        <div className="text-right text-sm font-medium text-gray-600 mt-1">
          {t('invoicing.total', 'Total')}: ₪{totalAmount.toLocaleString('he-IL', { minimumFractionDigits: 2 })}
        </div>
      </div>

      {/* Payment section for invoice-receipt */}
      {isInvoiceReceipt && (
        <div className="border-t pt-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">{t('invoicing.payment', 'Payment')}</label>
          <div className="grid grid-cols-2 gap-4">
            <select
              value={paymentType}
              onChange={(e) => setPaymentType(Number(e.target.value))}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value={2}>{t('invoicing.cash', 'Cash')}</option>
              <option value={1}>{t('invoicing.check', 'Check')}</option>
              <option value={4}>{t('invoicing.creditCard', 'Credit Card')}</option>
            </select>
            <input
              type="number"
              value={paymentAmount || totalAmount}
              onChange={(e) => setPaymentAmount(Number(e.target.value))}
              className="border rounded-md px-3 py-2 text-sm"
              step="0.01"
              min={0}
              placeholder={t('invoicing.paymentAmount', 'Amount')}
            />
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="bg-blue-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? t('common.loading', 'Loading...') : t('invoicing.createInvoice', 'Create Invoice')}
      </button>
    </form>
  );
}

// ── Create Receipt Form ──────────────────────────────────────────────

function CreateReceiptForm({
  receiptTypes,
  paymentTypes,
  customers,
  onCreated,
}: {
  receiptTypes: ReceiptType[];
  paymentTypes: PaymentTypeItem[];
  customers: Customer[];
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [receiptType, setReceiptType] = useState(1);
  const [customerId, setCustomerId] = useState(0);
  const [comments, setComments] = useState('');
  const [payments, setPayments] = useState([{ payment_type: 2, amount_nis: 0, description: '' }]);

  const addPayment = () => setPayments([...payments, { payment_type: 2, amount_nis: 0, description: '' }]);
  const removePayment = (i: number) => setPayments(payments.filter((_, idx) => idx !== i));
  const updatePayment = (i: number, field: string, value: string | number) =>
    setPayments(payments.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));

  const totalPayment = payments.reduce((sum, p) => sum + (p.amount_nis || 0), 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) {
      setError(t('invoicing.selectCustomer', 'Please select a customer'));
      return;
    }
    if (!payments.length || payments.every((p) => !p.amount_nis)) {
      setError(t('invoicing.addPayments', 'Please add at least one payment'));
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await createReceipt({
        receipt_type: receiptType,
        customer_id: customerId,
        comments: comments || undefined,
        payments: payments.map((p) => ({
          payment_type: p.payment_type,
          amount_nis: p.amount_nis,
          description: p.description || undefined,
        })),
        send_mail: false,
      });
      const data = res.data;
      setSuccess(
        `${t('invoicing.created', 'Created')} #${data.document_number}` +
          (data.document_link ? ` — PDF: ${data.document_link}` : '')
      );
      setTimeout(onCreated, 1500);
    } catch (e: unknown) {
      setError(String((e as Error).message || 'Failed to create receipt'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-5">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded text-sm">{success}</div>}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoicing.receiptType', 'Receipt Type')}</label>
          <select
            value={receiptType}
            onChange={(e) => setReceiptType(Number(e.target.value))}
            className="w-full border rounded-md px-3 py-2 text-sm"
          >
            {receiptTypes.map((rt) => (
              <option key={rt.receipt_type} value={rt.receipt_type}>
                {rt.receipt_name} ({rt.receipt_type})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoicing.customer', 'Customer')}</label>
          <select
            value={customerId}
            onChange={(e) => setCustomerId(Number(e.target.value))}
            className="w-full border rounded-md px-3 py-2 text-sm"
          >
            <option value={0}>-- {t('invoicing.selectCustomer', 'Select customer')} --</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.last_name} {c.first_name} ({c.customer_id})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoicing.comments', 'Comments')}</label>
        <input
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          className="w-full border rounded-md px-3 py-2 text-sm"
          maxLength={400}
        />
      </div>

      {/* Payments */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="text-sm font-medium text-gray-700">{t('invoicing.payments', 'Payments')}</label>
          <button type="button" onClick={addPayment} className="text-blue-600 text-sm hover:underline">
            + {t('invoicing.addPayment', 'Add payment')}
          </button>
        </div>
        {payments.map((p, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <select
              value={p.payment_type}
              onChange={(e) => updatePayment(i, 'payment_type', Number(e.target.value))}
              className="border rounded-md px-3 py-2 text-sm"
            >
              {paymentTypes.map((pt) => (
                <option key={pt.payment_type} value={pt.payment_type}>
                  {pt.payment_name}
                </option>
              ))}
            </select>
            <input
              type="number"
              value={p.amount_nis || ''}
              onChange={(e) => updatePayment(i, 'amount_nis', Number(e.target.value))}
              className="w-32 border rounded-md px-3 py-2 text-sm"
              step="0.01"
              min={0}
              placeholder="₪"
            />
            <input
              value={p.description}
              onChange={(e) => updatePayment(i, 'description', e.target.value)}
              className="flex-1 border rounded-md px-3 py-2 text-sm"
              placeholder={t('invoicing.description', 'Description')}
            />
            {payments.length > 1 && (
              <button type="button" onClick={() => removePayment(i)} className="text-red-400 hover:text-red-600 px-1">
                ✕
              </button>
            )}
          </div>
        ))}
        <div className="text-right text-sm font-medium text-gray-600 mt-1">
          {t('invoicing.total', 'Total')}: ₪{totalPayment.toLocaleString('he-IL', { minimumFractionDigits: 2 })}
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="bg-blue-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? t('common.loading', 'Loading...') : t('invoicing.createReceipt', 'Create Receipt')}
      </button>
    </form>
  );
}
